import React, { useEffect, useState } from "react";
import { ScrollText, RefreshCw, Download, ShieldCheck, Signature } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** AuditLogTab — append-only project audit trail. */
export default function AuditLogTab({ projectId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [signoff, setSignoff] = useState(null);
  const [chain, setChain] = useState(null);
  const [busy, setBusy] = useState(null);
  const [signForm, setSignForm] = useState({ license_no: "", firm: "", notes: "" });

  const apiRoot = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const refresh = () => {
    api.get(`/projects/${projectId}/audit-log?limit=500`).then((r) => setRows(r.data))
       .catch((e) => setErr(formatErr(e.response?.data?.detail) || e.message));
    api.get(`/projects/${projectId}/docs/lead-signoff`).then((r) => setSignoff(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/docs/audit/verify`).then((r) => setChain(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId]);

  const sign = async () => {
    setBusy("sign");
    try {
      await api.post(`/projects/${projectId}/docs/lead-signoff`, signForm);
      setSignForm({ license_no: "", firm: "", notes: "" });
      refresh();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  if (err) return <div className="pf-sev pf-sev-error" style={{ display: "block", padding: 12 }}>{err}</div>;
  if (rows === null) return <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>Loading audit log…</div>;
  const filtered = !filter.trim() ? rows : rows.filter((r) =>
    JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="audit-tab" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div>
          <div className="pf-overline">AUDIT LOG</div>
          <div className="pf-heading" style={{ fontSize: 20, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
            <ScrollText size={20} color="var(--pf-spark)" /> Append-only · {rows.length} events
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <input className="pf-input" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)}
                  style={{ fontSize: 12, padding: "6px 10px", width: 220 }} data-testid="audit-filter" />
          <a className="btn btn-ghost" href={`${apiRoot}/projects/${projectId}/docs/compliance-report`} target="_blank" rel="noreferrer" data-testid="audit-download-report">
            <Download size={13} /> Design Audit Report (7 sections)
          </a>
          <button className="btn btn-ghost" onClick={refresh} data-testid="audit-refresh"><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      {/* Hash-chain integrity strip */}
      {chain && (
        <div
          data-testid="audit-chain-status"
          className="pf-card"
          style={{
            padding: 10, display: "flex", alignItems: "center", gap: 10,
            borderColor: chain.ok ? "var(--pf-ok)" : "var(--pf-err)",
            fontSize: 12,
          }}
        >
          <ShieldCheck size={14} color={chain.ok ? "var(--pf-ok)" : "var(--pf-err)"} />
          <strong style={{ color: chain.ok ? "var(--pf-ok)" : "var(--pf-err)" }}>
            {chain.ok ? "Audit chain OK" : `BROKEN at seq ${chain.broken_at}`}
          </strong>
          <span style={{ color: "var(--pf-text-muted)" }}>
            · {chain.count || 0} entries{chain.last_hash ? ` · tail hash ${chain.last_hash.slice(0, 16)}…` : ""}
          </span>
        </div>
      )}

      {/* Lead Engineer sign-off */}
      <div className="pf-card" data-testid="lead-signoff-card" style={{ padding: 14 }}>
        <div className="pf-overline" style={{ marginBottom: 6 }}>LEAD ENGINEER SIGN-OFF</div>
        {signoff?.signer_name ? (
          <div style={{ fontSize: 13, color: "var(--pf-text)" }}>
            <Signature size={14} color="var(--pf-accent)" style={{ marginRight: 6 }} />
            Signed by <strong>{signoff.signer_name}</strong>
            {signoff.license_no ? ` (License ${signoff.license_no})` : ""}
            {signoff.firm ? ` · ${signoff.firm}` : ""}
            {signoff.signed_at ? ` · on ${signoff.signed_at.slice(0, 19).replace("T", " ")} UTC` : ""}
            <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-text-muted)", marginTop: 4 }}>
              hash {signoff.hash?.slice(0, 24)}…
            </div>
            {signoff.notes && <div style={{ fontSize: 12, color: "var(--pf-text-muted)", marginTop: 6 }}>{signoff.notes}</div>}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)", marginBottom: 8 }}>
            The project has not been signed off by a Lead Engineer yet. The sign-off seals the audit trail and is required before the project can be included in a PI insurance / PE-stamp package.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginTop: 10 }}>
          <input className="pf-input" placeholder="Registration / License no." value={signForm.license_no}
                 onChange={(e) => setSignForm({ ...signForm, license_no: e.target.value })}
                 data-testid="lead-signoff-license" />
          <input className="pf-input" placeholder="Firm / Practice" value={signForm.firm}
                 onChange={(e) => setSignForm({ ...signForm, firm: e.target.value })}
                 data-testid="lead-signoff-firm" />
          <input className="pf-input" placeholder="Notes (optional)" value={signForm.notes}
                 onChange={(e) => setSignForm({ ...signForm, notes: e.target.value })}
                 data-testid="lead-signoff-notes" />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" className="btn btn-primary" onClick={sign} disabled={busy === "sign"}
                  data-testid="lead-signoff-sign">
            <Signature size={13} /> {busy === "sign" ? "Signing…" : (signoff?.signer_name ? "Re-sign / supersede" : "Sign off project")}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: "var(--pf-text-muted)", padding: 24, textAlign: "center" }}>No audit events match the filter.</div>
      ) : (
        <div className="pf-card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--pf-surface-2)", borderBottom: "1px solid var(--pf-border)" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>Time (UTC)</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>Actor</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>Action</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>Target</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} data-testid={`audit-row-${i}`} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                  <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-text-muted)" }}>{r.at?.slice(0, 19).replace("T", " ")}</td>
                  <td style={{ padding: "6px 10px", fontSize: 11.5 }}>{r.actor_email || r.actor_id || "system"}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-spark)" }}>{r.action}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{r.target_type}/{r.target_id || ""}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--pf-text-muted)" }}>{r.metadata && Object.keys(r.metadata).length ? JSON.stringify(r.metadata) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
